import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, alpha } from '@mui/material/styles';
import {
  Box, Paper, IconButton, Avatar, Typography, Stack, TextField, Button, Collapse, Badge,
} from '@mui/material';
// components
import Iconify from './Iconify';
import axiosInstance from '../utils/axios';
// asset — brand kit 2026 (white icon for the green header)
import avatarImg from '../assets/images/logos/brand-2026/icon_white_360.png';

// ----------------------------------------------------------------------
// Live visitor chat. Live exchange runs through Redis on the backend; replies
// arrive in the agent's Telegram. The widget just talks to /livechat/* over the
// normal axios instance and polls while a conversation is active.
// Branding is human ("Helen"); there is no mention of any automated mechanism.

const STORAGE_KEY = 'hq_chat_cid';
const BRAND = '#00AD6F';
// Launcher uses a secondary (teal) tone so it reads as "help", not as the page CTA.
const HELP = '#297B78';
const HELP_HOVER = '#1f5f5d';
const POLL_MS = 3000;
const EMAIL_PROMPT_AFTER_MS = 60000; // ask for email if no reply in ~1 min

const GREETING = {
  seq: 0,
  sender: 'agent',
  text: '¡Hola! 🌿 Soy Helen, de Holistiqum. ¿En qué puedo acompañarte hoy?',
};

// El chat solo aparece donde el usuario puede COMPRAR: páginas de oferta de
// pago, tienda/checkout y las páginas "más" (upsell con botón de compra). Se
// excluye a propósito de los funnels gratis (nahual-gratis, */consulta, */gratis,
// lecturas regalo /r/:token, gracias, horóscopo, biblioteca, blog, overviews),
// donde la burbuja tapaba el resultado. Match por prefijo de pathname.
const PAID_PATH_PREFIXES = [
  // Tienda / checkout / membresías
  '/tienda',
  '/store',
  // Ofertas de pago por herramienta
  '/carta-nahual',
  '/tools/carta-nahual',
  '/tools/numerology/completa',
  '/tools/numerology/consulta-medida',
  '/tools/astralchart/completa',
  '/tools/suenos/profundo',
  '/tools/runas/tirada',
  '/tools/quiromancia/completa',
  '/tools/tarot/three-cards',
  // Páginas "más" / upsell con botón de compra
  '/tools/nahual/mas',
];

function isPaidPage(pathname) {
  const path = (pathname || '').replace(/\/+$/, '') || '/';
  return PAID_PATH_PREFIXES.some(
    (p) => path === p || path.startsWith(`${p}/`)
  );
}

const Panel = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 96,
  right: 24,
  width: 360,
  maxWidth: 'calc(100vw - 32px)',
  height: 520,
  maxHeight: 'calc(100vh - 140px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 16,
  zIndex: theme.zIndex.modal + 1,
  boxShadow: theme.customShadows?.z24 || '0 12px 40px rgba(0,0,0,0.18)',
  [theme.breakpoints.down('sm')]: {
    // En móvil el launcher vive arriba-izquierda; el panel baja desde ahí y
    // deja libre la parte inferior (donde están los CTA del resultado).
    top: 64,
    left: 8,
    right: 8,
    bottom: 'auto',
    width: 'auto',
    height: 'auto',
    maxHeight: 'calc(100vh - 80px)',
  },
}));

const Bubble = styled(Box)(({ theme, mine }) => ({
  maxWidth: '78%',
  padding: theme.spacing(1, 1.5),
  borderRadius: 14,
  fontSize: 14,
  lineHeight: 1.45,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  alignSelf: mine ? 'flex-end' : 'flex-start',
  backgroundColor: mine ? BRAND : theme.palette.grey[100],
  color: mine ? '#fff' : theme.palette.text.primary,
  borderBottomRightRadius: mine ? 4 : 14,
  borderBottomLeftRadius: mine ? 14 : 4,
}));

export default function LiveChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [cid, setCid] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [unread, setUnread] = useState(0);
  const [emailMode, setEmailMode] = useState(false);
  const [emailDone, setEmailDone] = useState(false);
  const [email, setEmail] = useState('');

  const lastSeq = useRef(0);
  const scroller = useRef(null);
  const firstVisitorAt = useRef(null);
  const gotReply = useRef(false);

  const scrollDown = useCallback(() => {
    requestAnimationFrame(() => {
      if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
    });
  }, []);

  const mergeIncoming = useCallback((incoming) => {
    if (!incoming?.length) return;
    setMessages((prev) => {
      const next = [...prev];
      incoming.forEach((m) => {
        if (m.seq > lastSeq.current) {
          next.push(m);
          lastSeq.current = m.seq;
        }
      });
      return next;
    });
    if (incoming.some((m) => m.sender === 'agent')) gotReply.current = true;
    scrollDown();
  }, [scrollDown]);

  // Ensure a conversation exists; returns its id.
  const ensureConversation = useCallback(async () => {
    if (cid) return cid;
    const { data } = await axiosInstance.post('/livechat/start/', {
      page_url: window.location.href,
    });
    localStorage.setItem(STORAGE_KEY, data.conversation_id);
    setCid(data.conversation_id);
    return data.conversation_id;
  }, [cid]);

  const resetConversation = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCid(null);
    lastSeq.current = 0;
    firstVisitorAt.current = null;
    gotReply.current = false;
    setMessages([GREETING]);
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    try {
      const id = await ensureConversation();
      const { data } = await axiosInstance.post('/livechat/send/', {
        conversation_id: id,
        text,
      });
      if (data?.message) {
        setMessages((prev) => [...prev, data.message]);
        lastSeq.current = Math.max(lastSeq.current, data.message.seq);
        if (!firstVisitorAt.current) firstVisitorAt.current = Date.now();
        scrollDown();
      }
    } catch (e) {
      if (e?.response?.status === 410) {
        resetConversation();
        // retry once on a fresh conversation
        try {
          const id = await ensureConversation();
          const { data } = await axiosInstance.post('/livechat/send/', { conversation_id: id, text });
          if (data?.message) { setMessages((prev) => [...prev, data.message]); lastSeq.current = data.message.seq; }
        } catch (_) { /* swallow */ }
      }
    }
  }, [input, ensureConversation, resetConversation, scrollDown]);

  const submitEmail = useCallback(async () => {
    const value = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value) || !cid) return;
    try {
      await axiosInstance.post('/livechat/email/', { conversation_id: cid, email: value });
      setEmailDone(true);
      setEmailMode(false);
      setMessages((prev) => [...prev, {
        seq: lastSeq.current + 0.5,
        sender: 'agent',
        text: '¡Gracias! Te escribiremos muy pronto a tu correo 💚',
      }]);
      scrollDown();
    } catch (_) { /* swallow */ }
  }, [email, cid, scrollDown]);

  // Poll while a conversation is active.
  useEffect(() => {
    if (!cid) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await axiosInstance.get('/livechat/poll/', {
          params: { conversation_id: cid, after: lastSeq.current },
        });
        if (!alive) return;
        const fresh = (data?.messages || []).filter((m) => m.sender !== 'visitor');
        if (fresh.length) {
          mergeIncoming(fresh);
          if (!open) setUnread((u) => u + fresh.length);
        }
      } catch (e) {
        if (e?.response?.status === 410) resetConversation();
      }
    };
    const handle = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(handle); };
  }, [cid, open, mergeIncoming, resetConversation]);

  // Offer the email capture if the visitor wrote and no reply came in ~1 min.
  useEffect(() => {
    if (emailDone || emailMode) return undefined;
    const handle = setInterval(() => {
      if (firstVisitorAt.current && !gotReply.current &&
          Date.now() - firstVisitorAt.current > EMAIL_PROMPT_AFTER_MS) {
        setEmailMode(true);
      }
    }, 5000);
    return () => clearInterval(handle);
  }, [emailDone, emailMode]);

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next) setUnread(0);
      return next;
    });
    scrollDown();
  };

  // Solo en páginas donde se puede comprar (ofertas/tienda/"más").
  if (!isPaidPage(pathname)) return null;

  return (
    <>
      <Collapse in={open} timeout={200} unmountOnExit>
        <Panel elevation={0}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1.5}
            sx={{ px: 2, py: 1.5, bgcolor: BRAND, color: '#fff' }}>
            <Avatar src={avatarImg} alt="Holistiqum" variant="square"
              sx={{ width: 40, height: 40, bgcolor: 'transparent', '& img': { objectFit: 'contain' } }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', lineHeight: 1.2 }}>Helen · Holistiqum</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#7CFFB2' }} />
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.85) }}>En línea</Typography>
              </Stack>
            </Box>
            <IconButton size="small" onClick={toggle} sx={{ color: '#fff' }}>
              <Iconify icon="eva:close-fill" width={22} height={22} />
            </IconButton>
          </Stack>

          {/* Messages */}
          <Box ref={scroller} sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#fff' }}>
            <Stack spacing={1.25}>
              {messages.map((m, i) => (
                <Bubble key={`${m.seq}-${i}`} mine={m.sender === 'visitor' ? 1 : 0}>{m.text}</Bubble>
              ))}
            </Stack>
          </Box>

          {/* Email capture */}
          {emailMode && !emailDone && (
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.100' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                Déjanos tu correo y te respondemos en cuanto estemos disponibles:
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField size="small" fullWidth placeholder="tu@correo.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitEmail()} />
                <Button variant="contained" onClick={submitEmail}
                  sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#009159' } }}>OK</Button>
              </Stack>
            </Box>
          )}

          {/* Input */}
          <Stack direction="row" alignItems="center" spacing={1}
            sx={{ p: 1.5, borderTop: (t) => `1px solid ${t.palette.divider}`, bgcolor: '#fff' }}>
            <TextField fullWidth size="small" placeholder="Escribe un mensaje…" value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
            <IconButton onClick={handleSend} disabled={!input.trim()}
              sx={{ bgcolor: BRAND, color: '#fff', '&:hover': { bgcolor: '#009159' },
                '&.Mui-disabled': { bgcolor: 'grey.300', color: '#fff' } }}>
              <Iconify icon="eva:paper-plane-fill" width={20} height={20} />
            </IconButton>
          </Stack>
        </Panel>
      </Collapse>

      {/* Launcher — smaller, secondary tone + helper label so it doesn't get confused with the page CTA.
          En móvil va arriba-izquierda (fuera de la zona del pulgar y de los CTA del resultado);
          en desktop se queda abajo-derecha. */}
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{
          position: 'fixed',
          top: { xs: 12, sm: 'auto' },
          left: { xs: 12, sm: 'auto' },
          bottom: { xs: 'auto', sm: 24 },
          right: { xs: 'auto', sm: 24 },
          zIndex: (t) => t.zIndex.modal + 1,
        }}>
        {!open && (
          <Paper elevation={0} onClick={toggle}
            sx={{ px: 1.5, py: 0.75, borderRadius: 999, cursor: 'pointer', maxWidth: '60vw',
              bgcolor: '#fff', color: 'text.secondary', fontSize: 13, fontWeight: 600,
              lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
            ¿Necesitas ayuda? Toca aquí
          </Paper>
        )}
        <Badge color="error" badgeContent={unread} overlap="circular">
          <IconButton onClick={toggle}
            sx={{ width: 48, height: 48, bgcolor: HELP, color: '#fff',
              boxShadow: '0 6px 18px rgba(41,123,120,0.4)',
              '&:hover': { bgcolor: HELP_HOVER } }}>
            <Iconify icon={open ? 'eva:close-fill' : 'eva:question-mark-circle-fill'} width={24} height={24} />
          </IconButton>
        </Badge>
      </Stack>
    </>
  );
}
